// src/pages/teacher/TeacherGradingPage.jsx
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Alert, LinearProgress,
  Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, Select, MenuItem, FormControl,
  InputLabel, Tooltip, CircularProgress, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField,
} from '@mui/material';
import GradeIcon     from '@mui/icons-material/Grade';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon   from '@mui/icons-material/Refresh';
import FeedbackIcon  from '@mui/icons-material/Feedback';
import { useTranslation }   from 'react-i18next';
import { getClassGrading, runGradingForStudent, sendFeedback } from '../../api/teacherApi';

export default function TeacherGradingPage() {
  const { t }        = useTranslation('teacher');
  const [results,    setResults]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [days,       setDays]       = useState(30);
  const [filter,     setFilter]     = useState('all');
  const [running,    setRunning]    = useState({});
  const [snack,      setSnack]      = useState('');
  const [feedbackDlg,setFeedbackDlg]= useState(null);
  const [fbMsg,      setFbMsg]      = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getClassGrading({ days });
      setResults(r.data?.results || []);
    } catch(e) {
      setError(e?.response?.data?.detail?.message || t('errorLoad'));
    } finally { setLoading(false); }
  }, [days, t]);

  useEffect(() => { load(); }, [load]);

  const handleRunGrading = async (resId) => {
    setRunning(r => ({ ...r, [resId]: true }));
    try {
      await runGradingForStudent(resId);
      setSnack(t('gradingLaunched'));
      await load();
    } catch(e) {
      setError(e?.response?.data?.detail?.message || 'Erreur grading');
    } finally { setRunning(r => ({ ...r, [resId]: false })); }
  };

  const handleSendFeedback = async () => {
    if (!feedbackDlg || !fbMsg.trim()) return;
    try {
      await sendFeedback(feedbackDlg.userId, fbMsg);
      setSnack(t('feedbackSent'));
      setFeedbackDlg(null);
      setFbMsg('');
    } catch(e) {
      setError(e?.response?.data?.detail?.message || 'Erreur feedback');
    }
  };

  const filtered = filter === 'all' ? results
    : results.filter(r => filter === 'passed' ? r.passed : !r.passed);

  const total    = results.length;
  const passed   = results.filter(r => r.passed).length;
  const rate     = total > 0 ? Math.round((passed/total)*100) : 0;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3, py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <GradeIcon sx={{ fontSize: 28, color: '#b45309' }} />
          <Typography variant="h5" fontWeight={700}>{t('grading')}</Typography>
        </Stack>
        <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
      </Stack>

      {/* Stats rapides */}
      <Stack direction="row" spacing={2} mb={3}>
        {[
          { label: t('totalExams'),  value: total,         color: '#1e293b' },
          { label: t('passed'),      value: passed,        color: '#16a34a' },
          { label: t('passRate'),    value: rate + '%',    color: rate >= 70 ? '#16a34a' : '#dc2626' },
        ].map(s => (
          <Paper key={s.label} variant="outlined"
            sx={{ flex: 1, p: 2, borderRadius: 2, textAlign: 'center' }}>
            <Typography fontSize={12} color="text.secondary">{s.label}</Typography>
            <Typography fontSize={22} fontWeight={700} color={s.color}>{s.value}</Typography>
          </Paper>
        ))}
      </Stack>

      {/* Filtres */}
      <Stack direction="row" spacing={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Periode</InputLabel>
          <Select value={days} onChange={e => setDays(e.target.value)} label="Periode">
            {[7,14,30,60].map(d => <MenuItem key={d} value={d}>{d}j</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Resultat</InputLabel>
          <Select value={filter} onChange={e => setFilter(e.target.value)} label="Resultat">
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="passed">Reussis</MenuItem>
            <MenuItem value="failed">Echoues</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell>{t('student')}</TableCell>
              <TableCell>{t('lab')}</TableCell>
              <TableCell>{t('score')}</TableCell>
              <TableCell>{t('result')}</TableCell>
              <TableCell>{t('trigger')}</TableCell>
              <TableCell>{t('date')}</TableCell>
              <TableCell>{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {t('noResults')}
                </TableCell>
              </TableRow>
            ) : filtered.map((r, i) => (
              <TableRow key={r.id || i} hover>
                <TableCell>
                  <Typography fontSize={13} fontWeight={600}>{r.full_name || r.username}</Typography>
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.lab_name}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LinearProgress variant="determinate" value={Math.min(r.score||0,100)}
                      sx={{ width: 60, height: 5, borderRadius: 2,
                        bgcolor: r.passed ? '#bbf7d0' : '#fecaca',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: r.passed ? '#16a34a' : '#ef4444' } }} />
                    <Typography fontSize={12} fontWeight={600}>
                      {(r.score||0).toFixed(1)}%
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip label={r.passed ? 'OK' : 'KO'}
                    color={r.passed ? 'success' : 'error'} size="small" />
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{r.trigger}</TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {r.graded_at ? new Date(r.graded_at).toLocaleString('fr') : '-'}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={t('rerunGrading')}>
                      <span>
                        <IconButton size="small"
                          onClick={() => handleRunGrading(r.reservation_id)}
                          disabled={!!running[r.reservation_id]}>
                          {running[r.reservation_id]
                            ? <CircularProgress size={14} />
                            : <PlayArrowIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    {!r.passed && (
                      <Tooltip title={t('sendFeedback')}>
                        <IconButton size="small"
                          onClick={() => setFeedbackDlg({ userId: r.user_id, name: r.full_name || r.username })}>
                          <FeedbackIcon fontSize="small" sx={{ color: '#4f46e5' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Dialog feedback */}
      <Dialog open={!!feedbackDlg} onClose={() => setFeedbackDlg(null)}
        maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>{t('feedbackTo', { name: feedbackDlg?.name })}</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={4} placeholder={t('feedbackPlaceholder')}
            value={fbMsg} onChange={e => setFbMsg(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFeedbackDlg(null)}>Annuler</Button>
          <Button variant="contained" disableElevation onClick={handleSendFeedback}
            disabled={!fbMsg.trim()}
            sx={{ borderRadius: 2, bgcolor: '#4f46e5', '&:hover': { bgcolor: '#4338ca' } }}>
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000}
        onClose={() => setSnack('')} message={snack} />
    </Box>
  );
}
